// Icons for the Nearby panel — inline SVG, drawn on a 24-grid in the Material
// Symbols idiom (the same visual family Google Maps uses, which is the map this
// sits on top of).
//
// Inline rather than an icon font or an SVG sprite because `src/pathfinder/`
// has to stay copy-out clean: no package to install, no asset to remember to
// copy, and nothing to fetch at runtime. Everything inherits `currentColor`, so
// a chip colours its icon by setting its own text colour.
//
// The `study` glyph is the UMaps reading figure — the same drawing the map pins
// use, so a row and its pin are recognisably the same thing.

const STROKE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.9,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

function Glyph({ name }) {
  switch (name) {
    // Person reading an open book — matches the map pin.
    case 'study':
      return (
        <g {...STROKE}>
          <circle cx="12" cy="4.9" r="2.6" />
          <path d="M7.6 11c1.1-1.3 2.7-2.1 4.4-2.1s3.3.8 4.4 2.1" />
          <path d="M3.4 10.2 12 13l8.6-2.8" />
          <path d="M3.4 10.2v9.1L12 22l8.6-2.7v-9.1" />
          <path d="M12 13v9" />
        </g>
      );
    // Stack of books.
    case 'library':
      return (
        <g {...STROKE}>
          <path d="M4 5.5h5.5a2 2 0 0 1 2 2V19a2 2 0 0 0-2-1.6H4z" />
          <path d="M20 5.5h-5.5a2 2 0 0 0-2 2V19a2 2 0 0 1 2-1.6H20z" />
        </g>
      );
    // Single figure — men's.
    case 'bathroom-mens':
      return (
        <g fill="currentColor">
          <circle cx="12" cy="3.9" r="2.1" />
          <path d="M12 6.8c-1.9 0-3.1 1.2-3.1 3v4.7h1.7V21h2.8v-6.5h1.7V9.8c0-1.8-1.2-3-3.1-3z" />
        </g>
      );
    // Single skirted figure — women's.
    case 'bathroom-womens':
      return (
        <g fill="currentColor">
          <circle cx="12" cy="3.9" r="2.1" />
          <path d="M12 6.8c-1.8 0-2.9 1-3.4 2.7l-1.5 5.6h2.3l.6-2.3V21h1.6v-5.2h.8V21h1.6v-8.2l.6 2.3h2.3l-1.5-5.6c-.5-1.7-1.6-2.7-3.4-2.7z" />
        </g>
      );
    // Two figures, one skirted — universal, and the generic fallback.
    case 'bathroom-universal':
    case 'bathroom':
      return (
        <g fill="currentColor">
          <circle cx="7.2" cy="4" r="1.9" />
          <path d="M7.2 6.6c-1.6 0-2.6 1-2.6 2.5v4h1.4V21h2.4v-7.9h1.4v-4c0-1.5-1-2.5-2.6-2.5z" />
          <circle cx="16.8" cy="4" r="1.9" />
          <path d="M16.8 6.6c-1.5 0-2.4.8-2.8 2.2l-1.2 4.6h1.8l.5-1.9V21h1.5v-4.4h.4V21h1.5v-9.5l.5 1.9h1.8l-1.2-4.6c-.4-1.4-1.3-2.2-2.8-2.2z" />
        </g>
      );
    // Microwave: body, door, keypad. Deliberately no feet and no plate inside —
    // both were tried on the contact sheet and both close up at 15px, where the
    // feet read as noise under the box and a plate reads as a camera lens.
    case 'microwave':
      return (
        <g {...STROKE}>
          <rect x="2.5" y="5.5" width="19" height="13" rx="1.5" />
          <rect x="4.8" y="8" width="9.8" height="8" rx="0.8" />
          <path d="M17.2 9.6h2.6M17.2 12.2h2.6" />
        </g>
      );
    // Bus seen head-on: windscreen split by a pillar, two lights, two wheels.
    // Front rather than side because it is what you see coming, and because a
    // side view at pin size collapses into a plain rounded rectangle.
    case 'bus':
      return (
        <g {...STROKE}>
          <rect x="4.6" y="3.5" width="14.8" height="13" rx="2.4" />
          <rect x="6.7" y="5.6" width="10.6" height="4.8" rx="0.9" />
          <path d="M12 5.6v4.8" />
          <path d="M6.9 13h2.2M14.9 13h2.2" />
          <circle cx="8.3" cy="18.9" r="1.5" />
          <circle cx="15.7" cy="18.9" r="1.5" />
        </g>
      );
    // Chair seen from the side — seat count.
    case 'seat':
      return (
        <g {...STROKE}>
          <path d="M5 11V5.5a1.5 1.5 0 0 1 3 0V11" />
          <path d="M5 11h11a2 2 0 0 1 2 2v2H5z" />
          <path d="M7 15v4M16 15v4" />
        </g>
      );
    // Chair seen face on — "chairs only" (no tables).
    case 'chair':
      return (
        <g {...STROKE}>
          <path d="M6.5 3.5h11v7h-11z" />
          <path d="M5 10.5h14v3H5z" />
          <path d="M7 13.5v6.5M17 13.5v6.5" />
        </g>
      );
    // Table seen from the side — the counterpart to `chair`.
    case 'table':
      return (
        <g {...STROKE}>
          <path d="M3 9.5h18" />
          <path d="M5.5 9.5V20M18.5 9.5V20" />
        </g>
      );
    case 'plug':
      return (
        <g {...STROKE}>
          <path d="M9 2.5v5M15 2.5v5" />
          <path d="M6 7.5h12v2.8a6 6 0 0 1-12 0z" />
          <path d="M12 16.3V21.5" />
        </g>
      );
    // Sofa — cushions.
    case 'cushion':
      return (
        <g {...STROKE}>
          <path d="M5 11V7.5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2V11" />
          <path d="M4 11a2 2 0 0 1 2 2v2h12v-2a2 2 0 0 1 2-2 2 2 0 0 1 2 2v5H2v-5a2 2 0 0 1 2-2z" />
        </g>
      );
    case 'quiet':
      return (
        <g {...STROKE}>
          <path d="M4 9.5h3.5L12 5.5v13L7.5 14.5H4z" />
          <path d="m16.5 9.5 4 5M20.5 9.5l-4 5" />
        </g>
      );
    case 'loud':
      return (
        <g {...STROKE}>
          <path d="M4 9.5h3.5L12 5.5v13L7.5 14.5H4z" />
          <path d="M15.6 9a4 4 0 0 1 0 6" />
          <path d="M18.2 6.6a7.5 7.5 0 0 1 0 10.8" />
        </g>
      );
    // Two people — group study rooms.
    case 'group':
      return (
        <g {...STROKE}>
          <circle cx="9" cy="7.5" r="3" />
          <path d="M3 20v-1a6 6 0 0 1 12 0v1" />
          <path d="M16.5 5a3 3 0 0 1 0 5.6" />
          <path d="M17.5 14.4A5 5 0 0 1 21 19v1" />
        </g>
      );
    case 'clock':
      return (
        <g {...STROKE}>
          <circle cx="12" cy="12" r="8.6" />
          <path d="M12 6.8V12l3.4 2.2" />
        </g>
      );
    // Wheelchair. The wheel is a plain circle rather than a swept arc — an arc
    // that nearly closes reads as a broken ring at 16px.
    case 'accessible':
      return (
        <g {...STROKE}>
          <circle cx="10.6" cy="3.5" r="1.8" />
          <path d="M10.1 5.8v4.4h4.3" />
          <path d="m14.4 10.2 2.8 7.6h2.6" />
          <circle cx="11.2" cy="14.9" r="5.3" />
        </g>
      );
    // Droplet — period products.
    case 'period':
      return (
        <g {...STROKE}>
          <path d="M12 3.2c3.2 4 5.6 6.8 5.6 9.8A5.6 5.6 0 0 1 6.4 13c0-3 2.4-5.8 5.6-9.8z" />
        </g>
      );
    // Binoculars — "look around from here". Deliberately the same shape as the
    // gold map pin's glyph, so the step-list button and the pin on the map read
    // as one feature.
    case 'look':
      return (
        // The eyepieces overlap the barrels rather than sitting above them —
        // with a gap the two halves read as a pair of figures, not one object.
        <g fill="currentColor">
          <rect x="3.0" y="8.4" width="7.6" height="12.6" rx="3.4" />
          <rect x="13.4" y="8.4" width="7.6" height="12.6" rx="3.4" />
          <rect x="9.9" y="11.4" width="4.2" height="3.2" />
          <rect x="4.4" y="3.4" width="4.8" height="7.2" rx="1.7" />
          <rect x="14.8" y="3.4" width="4.8" height="7.2" rx="1.7" />
        </g>
      );
    // Box with an arrow leaving it — the generic share mark.
    case 'share':
      return (
        <g {...STROKE}>
          <path d="M12 15V3.4" />
          <path d="M8.4 6.8 12 3.2l3.6 3.6" />
          <path d="M6.5 10.5H5a1.5 1.5 0 0 0-1.5 1.5v7.5A1.5 1.5 0 0 0 5 21h14a1.5 1.5 0 0 0 1.5-1.5V12a1.5 1.5 0 0 0-1.5-1.5h-1.5" />
        </g>
      );
    case 'chevron-left':
      return <g {...STROKE}><path d="M15 4.5 7.5 12l7.5 7.5" /></g>;
    case 'chevron-right':
      return <g {...STROKE}><path d="m9 4.5 7.5 7.5L9 19.5" /></g>;
    case 'close':
      return <g {...STROKE}><path d="M5.5 5.5 18.5 18.5M18.5 5.5 5.5 18.5" /></g>;
    case 'plus':
      return <g {...STROKE}><path d="M12 5.5v13M5.5 12h13" /></g>;
    // Camera with a "+" where the shutter button would be — the invitation to
    // add the first photo of a place, not a viewfinder for one that has some.
    case 'camera':
      return (
        <g {...STROKE}>
          <path d="M3.2 8.6h3.4l1.5-2.4h7.8l1.5 2.4h3.4a1 1 0 0 1 1 1v8.4a1 1 0 0 1-1 1H3.2a1 1 0 0 1-1-1V9.6a1 1 0 0 1 1-1z" />
          <circle cx="12" cy="13.6" r="3.5" />
          <path d="M12 11.9v3.4M10.3 13.6h3.4" />
        </g>
      );
    // Map pin — the panel's own button.
    case 'pin':
      return (
        <g {...STROKE}>
          <path d="M12 21.5s7-7.4 7-11.4a7 7 0 1 0-14 0c0 4 7 11.4 7 11.4z" />
          <circle cx="12" cy="9.8" r="2.6" />
        </g>
      );
    default:
      return null;
  }
}

export default function PlaceIcon({ name, size = 16, className }) {
  return (
    <svg className={className} width={size} height={size} viewBox="0 0 24 24"
         aria-hidden="true" focusable="false">
      <Glyph name={name} />
    </svg>
  );
}
